import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MessageSquare, ArrowLeft, Bot, User, Trash2, Plus, X, Search } from 'lucide-react';

const POLL_CONVS_MS = 30000;
const POLL_MSGS_MS = 10000;

function _timeAgo(isoStr) {
  if (!isoStr) return '';
  try {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    return `hace ${Math.floor(diff / 86400)}d`;
  } catch {
    return '';
  }
}

function ConversationList({ conversations, selectedId, onSelect, loading, error }) {
  if (loading && !conversations.length) {
    return <div className="p-4 text-sm text-zinc-500">Cargando…</div>;
  }
  if (error) {
    return <div className="p-4 text-sm text-red-500">{error}</div>;
  }
  if (!conversations.length) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
        <p className="text-sm text-zinc-500">Sin conversaciones</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-zinc-800">
      {conversations.map((conv) => (
        <li
          key={conv.id}
          className={`px-4 py-3 cursor-pointer transition-colors ${
            selectedId === conv.id
              ? 'bg-zinc-800'
              : 'hover:bg-zinc-800/60'
          }`}
          onClick={() => onSelect(conv)}
        >
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-zinc-400" />
              </div>
              <span className="font-medium text-sm text-zinc-100 truncate">
                {conv.displayName || conv.phone}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {conv.unreadCount > 0 && (
                <Badge variant="destructive" className="text-xs px-1.5 py-0.5 h-4">
                  {conv.unreadCount}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={`text-xs h-4 px-1.5 ${
                  conv.mode === 'HUMAN'
                    ? 'border-green-600 text-green-400 bg-green-400/10'
                    : 'border-zinc-600 text-zinc-400'
                }`}
              >
                {conv.mode === 'HUMAN' ? 'Humano' : 'IA'}
              </Badge>
            </div>
          </div>
          <div className="text-xs text-zinc-500 ml-9">{_timeAgo(conv.lastMessageAt)}</div>
        </li>
      ))}
    </ul>
  );
}

function MessageBubble({ msg }) {
  const isInbound = msg.direction === 'INBOUND';
  const isSystem = msg.source === 'SYSTEM';
  const isHuman = msg.source === 'HUMAN';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-zinc-500 bg-zinc-800 rounded-full px-3 py-0.5">
          {msg.text}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex ${isInbound ? 'justify-start' : 'justify-end'} my-1.5`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          isInbound
            ? 'bg-zinc-800 text-zinc-100 rounded-tl-sm'
            : isHuman
            ? 'bg-green-600 text-white rounded-tr-sm'
            : 'bg-blue-600 text-white rounded-tr-sm'
        }`}
      >
        {!isInbound && (
          <div className="flex items-center gap-1 mb-0.5">
            {isHuman ? (
              <User className="w-3 h-3 opacity-75" />
            ) : (
              <Bot className="w-3 h-3 opacity-75" />
            )}
            <span className="text-xs opacity-75">
              {isHuman ? (msg.createdByUserName || 'Equipo') : 'IA'}
            </span>
          </div>
        )}
        <p className="leading-snug whitespace-pre-wrap break-words">{msg.text}</p>
      </div>
    </div>
  );
}

function DeleteModal({ onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-sm shadow-xl">
        <h3 className="font-semibold text-zinc-100 mb-2">¿Eliminar esta conversación?</h3>
        <p className="text-sm text-zinc-400 mb-5">
          Se borrarán todos los mensajes. Esta acción no se puede deshacer.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WhatsAppInbox() {
  const { user } = useAuth();
  const canDelete = user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [convError, setConvError] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [sendText, setSendText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [botPaused, setBotPaused] = useState(false);
  const [togglingBotPause, setTogglingBotPause] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [initWarn, setInitWarn] = useState(null);
  const [convSearch, setConvSearch] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sendingTemplate, setSendingTemplate]     = useState(false);
  const [templateError, setTemplateError]         = useState('');
  const [initError, setInitError]                 = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const knownMsgIds = useRef(new Set());

  useEffect(() => {
    api.get('/my-subscription')
      .then((res) => setPlan((res.data?.plan || 'BASIC').toUpperCase()))
      .catch(() => setPlan('BASIC'))
      .finally(() => setPlanLoading(false));
  }, []);

  const fetchConversations = useCallback(async (isBackground = false) => {
    try {
      const resp = await api.get('/whatsapp/conversations', { params: { limit: 50 } });
      const fresh = resp.data;
      setConversations(fresh);
      setConvError(null);
      // Sync selectedConv metadata (unreadCount, mode, etc.) without losing user selection
      if (isBackground) {
        setSelectedConv((prev) => {
          if (!prev) return prev;
          const updated = fresh.find((c) => c.id === prev.id);
          return updated ? { ...prev, ...updated } : prev;
        });
      }
    } catch {
      setConvError('Error al cargar conversaciones');
    } finally {
      setConvLoading(false);
    }
  }, []);

  // Incremental message fetch — only appends new messages, no flicker
  const fetchMessagesIncremental = useCallback(async (convId) => {
    try {
      const resp = await api.get(`/whatsapp/conversations/${convId}/messages`);
      const fresh = resp.data;
      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newOnes = fresh.filter((m) => !existingIds.has(m.id));
        if (newOnes.length === 0) return prev;
        return [...prev, ...newOnes];
      });
      setMsgError(null);
    } catch {
      setMsgError('Error al cargar mensajes');
    }
  }, []);

  const fetchMessagesInitial = useCallback(async (convId) => {
    setMsgLoading(true);
    setMsgError(null);
    knownMsgIds.current = new Set();
    try {
      const resp = await api.get(`/whatsapp/conversations/${convId}/messages`);
      setMessages(resp.data);
    } catch {
      setMsgError('Error al cargar mensajes');
    } finally {
      setMsgLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations(false);
    const t = setInterval(() => fetchConversations(true), POLL_CONVS_MS);
    return () => clearInterval(t);
  }, [fetchConversations]);

  useEffect(() => {
    api.get('/whatsapp/bot-pause').then((r) => setBotPaused(r.data.paused)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showInitiateModal) { setClientSearch(''); setClientResults([]); return; }
  }, [showInitiateModal]);

  useEffect(() => {
    if (!clientSearch.trim()) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await api.get('/clients', { params: { search: clientSearch.trim(), limit: 10 } });
        setClientResults(r.data);
      } catch { setClientResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const handleConfirmBotPause = useCallback(async (paused) => {
    setShowPauseModal(false);
    setTogglingBotPause(true);
    try {
      await api.put('/whatsapp/bot-pause', { paused });
      setBotPaused(paused);
      fetchConversations(false);
    } catch { /* silent — state unchanged */ }
    finally { setTogglingBotPause(false); }
  }, [fetchConversations]);

  const handleToggleBotPause = useCallback(() => {
    if (!botPaused) { setShowPauseModal(true); } else { handleConfirmBotPause(false); }
  }, [botPaused, handleConfirmBotPause]);

  const handleInitiateConversation = useCallback(async (client) => {
    setInitiating(true);
    try {
      const resp = await api.post('/whatsapp/conversations/initiate', {
        clientId: client.id,
        phone: client.phone,
        displayName: `${client.fullName || ''} ${client.surname || ''}`.trim(),
      });
      const { conversation: conv, templateSent } = resp.data;
      setConversations((prev) => {
        const exists = prev.find((c) => c.id === conv.id);
        return exists ? prev.map((c) => (c.id === conv.id ? conv : c)) : [conv, ...prev];
      });
      setShowInitiateModal(false);
      setSelectedConv(conv);
      setMessages([]);
      setSendText('');
      setSendError(null);
      setInitWarn(
        templateSent
          ? null
          : 'No se pudo enviar el mensaje de bienvenida. Puedes escribir manualmente cuando el cliente responda.'
      );
      setMobileView('messages');
      setTimeout(() => textareaRef.current?.focus(), 50);
    } catch (err) {
      const conv = err?.response?.data?.conversation;
      if (conv) {
        // 422 con conversación creada en DB — tratar como éxito con aviso
        setConversations(prev => {
          const exists = prev.find(c => c.id === conv.id);
          return exists ? prev : [conv, ...prev];
        });
        setShowInitiateModal(false);
        setInitError('');
        setSelectedConv(conv);
        setMessages([]);
        setSendText('');
        setMobileView('messages');
        setInitWarn(
          err?.response?.data?.templateError ||
          'No se pudo enviar el mensaje de bienvenida. Puedes escribir manualmente cuando el cliente responda.'
        );
      } else {
        // Error real sin conversación (400, 403, 404, red)
        setInitError(
          err?.response?.data?.detail ||
          'Error al iniciar la conversación. Inténtalo de nuevo.'
        );
      }
    } finally { setInitiating(false); }
  }, []);

  useEffect(() => {
    if (!selectedConv?.id) return;
    fetchMessagesInitial(selectedConv.id);
    const t = setInterval(() => fetchMessagesIncremental(selectedConv.id), POLL_MSGS_MS);
    return () => clearInterval(t);
  }, [selectedConv?.id, fetchMessagesInitial, fetchMessagesIncremental]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectConv = useCallback((conv) => {
    setSelectedConv(conv);
    setMessages([]);
    setSendText('');
    setSendError(null);
    setInitWarn(null);
    setMobileView('messages');
    if (conv.unreadCount > 0) {
      api.put(`/whatsapp/conversations/${conv.id}/read`).catch(() => {});
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c))
      );
    }
  }, []);

  const handleBack = useCallback(() => {
    setMobileView('list');
  }, []);

  const handleSetMode = useCallback(async (mode) => {
    if (!selectedConv) return;
    try {
      const resp = await api.put(`/whatsapp/conversations/${selectedConv.id}/mode`, { mode });
      const updated = resp.data;
      setSelectedConv(updated);
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      fetchMessagesInitial(updated.id);
    } catch {
      setSendError('Error al cambiar modo');
    }
  }, [selectedConv, fetchMessagesInitial]);

  const handleSendTemplate = async () => {
    setSendingTemplate(true);
    setTemplateError('');
    try {
      await api.post(`/whatsapp/conversations/${selectedConv.id}/send-template`);
      setShowTemplateModal(false);
      await fetchMessagesInitial(selectedConv.id);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        setTemplateError('Sin créditos de plantilla disponibles. Activa la recarga automática.');
      } else if (status === 422) {
        setTemplateError('Meta rechazó la plantilla. Verifica que esté aprobada en tu WABA.');
      } else {
        setTemplateError('Error al enviar. Inténtalo de nuevo.');
      }
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleSend = useCallback(async () => {
    if (!selectedConv || !sendText.trim() || selectedConv.mode !== 'HUMAN') return;
    setSending(true);
    setSendError(null);
    try {
      const resp = await api.post(
        `/whatsapp/conversations/${selectedConv.id}/messages`,
        { text: sendText.trim() },
      );
      setMessages((prev) => [...prev, resp.data]);
      setSendText('');
    } catch (err) {
      setSendError(err?.response?.data?.detail || 'Error al enviar el mensaje');
    } finally {
      setSending(false);
    }
  }, [selectedConv, sendText]);

  const handleDeleteConv = useCallback(async () => {
    if (!selectedConv) return;
    setDeleting(true);
    try {
      await api.delete(`/whatsapp/conversations/${selectedConv.id}`);
      setConversations((prev) => prev.filter((c) => c.id !== selectedConv.id));
      setSelectedConv(null);
      setMessages([]);
      setShowDeleteModal(false);
      setMobileView('list');
    } catch {
      setSendError('Error al eliminar la conversación');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  }, [selectedConv]);

  const windowOpen = Boolean(
    selectedConv?.lastInboundAt &&
    (Date.now() - new Date(selectedConv.lastInboundAt).getTime()) < 86400000
  );
  const canSend = selectedConv?.mode === 'HUMAN' && sendText.trim().length > 0 && !sending && windowOpen;

  if (planLoading) {
    return <div className="p-8 text-sm text-zinc-500">Cargando…</div>;
  }

  if (plan === 'BASIC') {
    return (
      <div
        data-testid="wa-upgrade-screen"
        className="flex flex-col items-center justify-center h-[calc(100vh-120px)] gap-4 text-center px-4"
      >
        <MessageSquare className="w-10 h-10 text-zinc-600" />
        <p className="text-base font-medium text-zinc-300">
          El inbox de WhatsApp está disponible en los planes Expert y Premium.
        </p>
        <p className="text-sm text-zinc-500">
          Contacta con tu gestor de AdamSched para cambiar de plan.
        </p>
      </div>
    );
  }

  return (
    <>
      {showDeleteModal && (
        <DeleteModal
          onConfirm={handleDeleteConv}
          onCancel={() => setShowDeleteModal(false)}
          loading={deleting}
        />
      )}

      {showPauseModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-zinc-100 mb-2">¿Activar modo humano global?</h3>
            <p className="text-sm text-zinc-400 mb-5">
              La IA dejará de responder a todos los clientes. Todas las conversaciones pasarán a modo humano.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setShowPauseModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => handleConfirmBotPause(true)}
              >
                Activar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showInitiateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="wa-initiate-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-zinc-100">Nueva conversación</h3>
              <button onClick={() => { setShowInitiateModal(false); setInitError(''); }} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                autoFocus
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                placeholder="Buscar cliente por nombre o teléfono…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                data-testid="wa-client-search-input"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-0.5">
              {searchLoading && <p className="text-xs text-zinc-500 py-2 text-center">Buscando…</p>}
              {!searchLoading && clientSearch && clientResults.length === 0 && (
                <p className="text-xs text-zinc-500 py-2 text-center">Sin resultados</p>
              )}
              {clientResults.map((cl) => (
                <button
                  key={cl.id}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  disabled={!cl.phone || initiating}
                  onClick={() => handleInitiateConversation(cl)}
                  data-testid="wa-client-result"
                >
                  <p className="text-sm text-zinc-100">{cl.fullName} {cl.surname}</p>
                  <p className="text-xs text-zinc-500">{cl.phone || 'Sin teléfono'}</p>
                </button>
              ))}
            </div>
            {initError && (
              <p className="text-sm text-red-500 mt-2 mb-1">{initError}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex h-[calc(100vh-120px)] border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900 shadow-sm">
        {/* Left panel — conversation list */}
        <div
          className={`
            w-full md:w-72 md:flex flex-col shrink-0 border-r border-zinc-800
            ${mobileView === 'list' ? 'flex' : 'hidden'}
          `}
        >
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-zinc-400 shrink-0" />
            <span className="font-semibold text-sm text-zinc-100">WhatsApp</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                data-testid="wa-bot-pause-toggle"
                onClick={handleToggleBotPause}
                disabled={togglingBotPause}
                title={botPaused ? 'Cambiar a modo IA' : 'Cambiar a modo humano'}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                  botPaused
                    ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/40'
                    : 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/40'
                }`}
              >
                {botPaused ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                {botPaused ? 'Modo humano' : 'IA activa'}
              </button>
              <button
                data-testid="wa-initiate-btn"
                className="w-6 h-6 rounded-full flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors"
                onClick={() => { setShowInitiateModal(true); setInitError(''); }}
                title="Iniciar conversación"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              {convLoading && conversations.length > 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 animate-pulse" />
              )}
            </div>
          </div>
          <div className="px-3 py-2 border-b border-zinc-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
              <input
                type="text"
                value={convSearch}
                onChange={(e) => setConvSearch(e.target.value)}
                placeholder="Buscar por nombre o teléfono..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                data-testid="wa-conv-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {(() => {
              const q = convSearch.toLowerCase().trim();
              const filtered = q
                ? conversations.filter(
                    (c) =>
                      (c.displayName || '').toLowerCase().includes(q) ||
                      (c.phone || '').includes(q)
                  )
                : conversations;
              if (!convLoading && !convError && q && filtered.length === 0) {
                return (
                  <div className="p-4 text-sm text-zinc-500 text-center">
                    No se encontraron conversaciones
                  </div>
                );
              }
              return (
                <ConversationList
                  conversations={filtered}
                  selectedId={selectedConv?.id}
                  onSelect={handleSelectConv}
                  loading={convLoading}
                  error={convError}
                />
              );
            })()}
          </div>
        </div>

        {/* Right panel — messages */}
        <div
          className={`
            flex-1 flex flex-col min-w-0
            ${mobileView === 'messages' ? 'flex' : 'hidden md:flex'}
          `}
        >
          {!selectedConv ? (
            <div className="flex-1 flex items-center justify-center text-zinc-600 text-sm">
              <div className="text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
                <p>Selecciona una conversación</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3">
                <button
                  className="md:hidden text-zinc-400 hover:text-zinc-200 transition-colors"
                  onClick={handleBack}
                  aria-label="Volver"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-zinc-100 truncate">
                      {selectedConv.displayName || selectedConv.phone}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-xs h-4 px-1.5 shrink-0 ${
                        selectedConv.mode === 'HUMAN'
                          ? 'border-green-600 text-green-400 bg-green-400/10'
                          : 'border-zinc-600 text-zinc-400'
                      }`}
                    >
                      {selectedConv.mode === 'HUMAN' ? 'Humano' : 'IA'}
                    </Badge>
                  </div>
                  {selectedConv.displayName && (
                    <p className="text-xs text-zinc-500 truncate">{selectedConv.phone}</p>
                  )}
                </div>

                <div className="flex gap-2 shrink-0 items-center">
                  {selectedConv.mode === 'AI' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs h-7"
                      onClick={() => handleSetMode('HUMAN')}
                    >
                      Tomar conversación
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs h-7"
                      onClick={() => handleSetMode('AI')}
                    >
                      Devolver a IA
                    </Button>
                  )}
                  {canDelete && (
                    <button
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                      onClick={() => setShowDeleteModal(true)}
                      aria-label="Eliminar conversación"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5">
                {msgLoading && !messages.length && (
                  <div className="text-xs text-zinc-600 text-center py-4">Cargando mensajes…</div>
                )}
                {msgError && (
                  <div className="text-xs text-red-400 text-center py-4">{msgError}</div>
                )}
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} msg={msg} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Template modal */}
              {showTemplateModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Enviar plantilla de reactivación
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 bg-gray-50 rounded-lg p-3">
                      Hola <strong>{selectedConv?.displayName || selectedConv?.phone}</strong>,
                      te escribimos desde el centro <strong>[nombre del centro]</strong>. ¿Cómo estás?
                    </p>
                    <p className="text-xs text-gray-400 mb-4">
                      Consume 1 crédito de plantilla (wa_pay_reminders).
                    </p>
                    {templateError && (
                      <p className="text-sm text-red-600 mb-3">{templateError}</p>
                    )}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setShowTemplateModal(false); setTemplateError(''); }}
                        disabled={sendingTemplate}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSendTemplate}
                        disabled={sendingTemplate}
                        className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {sendingTemplate ? 'Enviando…' : 'Enviar plantilla'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Composer */}
              <div className="border-t border-zinc-800 px-4 py-3">
                {selectedConv.mode === 'AI' && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-2">
                    <Bot className="w-3.5 h-3.5" />
                    La IA está gestionando esta conversación. Toma la conversación para responder.
                  </div>
                )}
                {initWarn && (
                  <div className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-2" data-testid="wa-template-warn">
                    {initWarn}
                  </div>
                )}
                {sendError && (
                  <div className="text-xs text-red-400 mb-1">{sendError}</div>
                )}
                {selectedConv.mode === 'HUMAN' && !windowOpen && (
                  <div className="flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-2">
                    <p className="text-xs text-amber-400">
                      La ventana de 24h ha cerrado. Solo puedes enviar plantillas aprobadas.
                    </p>
                    <button
                      className="text-xs text-amber-300 underline whitespace-nowrap hover:text-amber-200"
                      onClick={() => { setShowTemplateModal(true); setTemplateError(''); }}
                    >
                      Enviar plantilla
                    </button>
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={textareaRef}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 resize-none focus:outline-none focus:ring-1 focus:ring-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    rows={2}
                    placeholder={
                      selectedConv.mode !== 'HUMAN'
                        ? 'Toma la conversación para escribir'
                        : !windowOpen
                        ? 'Ventana de 24h cerrada — usa una plantilla'
                        : 'Escribe un mensaje…'
                    }
                    disabled={selectedConv.mode !== 'HUMAN' || !windowOpen}
                    value={sendText}
                    onChange={(e) => setSendText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && canSend) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-[52px] px-4 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
                    onClick={handleSend}
                    disabled={!canSend}
                  >
                    Enviar
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default WhatsAppInbox;
